import type { Page } from "../schema/deck";
import type { Animation, AnimationTarget } from "../schema/timeline";
import type { Color } from "../schema/primitives";
import type { Element } from "../schema/elements";
import type { Step } from "./compile";

/** `elementId` for the whole shape, `elementId#p3` for paragraph 3. */
export type TargetKey = string;

export interface TargetState {
  visible: boolean;
  opacity: number;
  translate: { x: number; y: number };
  scale: { x: number; y: number };
  rotate: number;
  fill?: Color;
  textColor?: Color;
  strokeColor?: Color;
  media?: { playing: boolean; position: number };
}

export const identityState = (visible = true): TargetState => ({
  visible,
  opacity: 1,
  translate: { x: 0, y: 0 },
  scale: { x: 1, y: 1 },
  rotate: 0,
});

export function targetKeys(target: AnimationTarget): TargetKey[] {
  if (target.paragraphs) {
    const keys: TargetKey[] = [];
    for (let p = target.paragraphs.from; p <= target.paragraphs.to; p++) keys.push(`${target.elementId}#p${p}`);
    return keys;
  }
  return [target.elementId];
}

function isEntrance(anim: Animation): boolean {
  const e = anim.effect;
  return "phase" in e && e.phase === "in";
}
function isExit(anim: Animation): boolean {
  const e = anim.effect;
  return "phase" in e && e.phase === "out";
}

export function* walkElements(elements: Element[]): Generator<Element> {
  for (const el of elements) {
    yield el;
    if (el.kind === "group") yield* walkElements(el.children);
  }
}

/** Resting state before any step: a target is hidden iff the first animation touching it is an entrance. */
export function initialState(page: Page): Map<TargetKey, TargetState> {
  const state = new Map<TargetKey, TargetState>();
  for (const el of walkElements(page.elements)) {
    state.set(el.id, identityState(!el.hidden));
    if (el.kind === "media") {
      state.get(el.id)!.media = { playing: false, position: el.trim.startAt };
    }
  }
  const firstSeen = new Set<TargetKey>();
  // Targets whose first animation is an entrance start hidden; that holds for click-triggered sequences too.
  const all = [...page.timeline.main, ...page.timeline.interactive.flatMap((s) => s.animations)];
  for (const anim of all) {
    for (const key of targetKeys(anim.target)) {
      if (firstSeen.has(key)) continue;
      firstSeen.add(key);
      if (isEntrance(anim)) {
        state.set(key, { ...(state.get(key) ?? identityState()), visible: false });
      } else if (!state.has(key)) {
        state.set(key, identityState(true));
      }
    }
  }
  return state;
}

function cloneState(s: TargetState): TargetState {
  return {
    ...s,
    translate: { ...s.translate },
    scale: { ...s.scale },
    media: s.media ? { ...s.media } : undefined,
  };
}

/** Apply the resting result of one animation to a target state (mutates). */
export function applyEffectEnd(state: TargetState, anim: Animation, page?: Page): void {
  const e = anim.effect;
  if (anim.rewindAfter && e.kind !== "media") return;
  if (isEntrance(anim)) {
    state.visible = true;
    state.opacity = 1;
    state.translate = { x: 0, y: 0 };
    state.scale = { x: 1, y: 1 };
    return;
  }
  if (isExit(anim)) {
    state.visible = false;
    if (state.media) state.media.playing = false;
    return;
  }
  switch (e.kind) {
    case "growShrink":
      state.scale = { x: state.scale.x * e.scaleX, y: state.scale.y * e.scaleY };
      return;
    case "spin":
      state.rotate = (state.rotate + e.degrees) % 360;
      return;
    case "transparency":
      state.opacity = e.opacity;
      return;
    case "colorChange":
      if (!e.persist) return;
      if (e.property === "fill") state.fill = e.to;
      else if (e.property === "line") state.strokeColor = e.to;
      else state.textColor = e.to;
      return;
    case "motionPath": {
      let x = 0, y = 0;
      for (const c of e.path) if (c.c !== "Z") { x = c.x; y = c.y; }
      state.translate = { x: state.translate.x + x, y: state.translate.y + y };
      return;
    }
    case "media": {
      const m = state.media ?? { playing: false, position: 0 };
      switch (e.action) {
        case "play":
        case "resume":
          m.playing = true;
          break;
        case "playFrom":
          m.playing = true;
          m.position = e.at ?? m.position;
          break;
        case "pause":
          m.playing = false;
          break;
        case "stop": {
          m.playing = false;
          let startAt = 0;
          if (page) {
            for (const el of walkElements(page.elements)) {
              if (el.id === anim.target.elementId && el.kind === "media") startAt = el.trim.startAt;
            }
          }
          m.position = startAt;
          break;
        }
        case "togglePause":
          m.playing = !m.playing;
          break;
      }
      state.media = m;
      return;
    }
    default:
      return; // pulse, teeter, boldFlash, underline, fallback(emph): no resting change
  }
}

/** Resting state after steps[0..n] have completed. n = -1 gives the initial state. */
export function stateAfter(page: Page, steps: Step[], n: number): Map<TargetKey, TargetState> {
  const state = new Map<TargetKey, TargetState>();
  for (const [k, v] of initialState(page)) state.set(k, cloneState(v));
  for (let i = 0; i <= n && i < steps.length; i++) {
    for (const item of steps[i]!.items) {
      for (const key of targetKeys(item.anim.target)) {
        const s = state.get(key) ?? identityState(true);
        applyEffectEnd(s, item.anim, page);
        state.set(key, s);
      }
    }
  }
  return state;
}
